/**
 * Mapbox Directions REST client for the Route Planner.
 *
 * Plain `fetch` wrapper around the `/directions/v5/mapbox/driving` endpoint.
 * Returns the raw GeoJSON LineString plus distance/duration totals.
 * The response is requested with `overview=simplified` — the plan uses a
 * simplified line for corridor filtering (cheap point-to-line distance) and
 * only buffers a polygon for visualization in Phase 3+.
 *
 * All callers should pass an `AbortSignal` so stale requests can be cancelled
 * when the waypoint list changes mid-flight.
 */

import config from '../../config';

const DIRECTIONS_BASE = 'https://api.mapbox.com/directions/v5/mapbox/driving';

/**
 * @typedef {{ lng: number, lat: number }} LngLat
 */

/**
 * @typedef {object} DirectionsResult
 * @property {import('geojson').LineString} geometry
 * @property {number} distanceMeters
 * @property {number} durationSeconds
 */

/**
 * Format a list of waypoints into the `{lng},{lat};{lng},{lat};...` segment
 * the Mapbox Directions API expects. Caller is responsible for enforcing the
 * 12-waypoint limit — we throw if exceeded because it's a programming bug,
 * not a user-recoverable error.
 *
 * @param {LngLat[]} waypoints
 * @returns {string}
 */
const encodeCoordinates = (waypoints) => {
  if (!Array.isArray(waypoints) || waypoints.length < 2) {
    throw new Error('fetchDirections: need at least 2 waypoints');
  }
  if (waypoints.length > 12) {
    throw new Error('fetchDirections: Mapbox Directions allows at most 12 coordinates');
  }
  return waypoints.map(({ lng, lat }) => `${lng},${lat}`).join(';');
};

/**
 * Call the Mapbox Directions API for the given ordered waypoint list.
 *
 * @param {{
 *   waypoints: LngLat[],
 *   preferences?: {
 *     avoidMotorway?: boolean,
 *     avoidTolls?: boolean,
 *   },
 *   signal?: AbortSignal
 * }} params
 * @returns {Promise<DirectionsResult>}
 */
export const fetchDirections = async ({ waypoints, preferences = {}, signal }) => {
  const token = config.maps.mapboxAccessToken;
  if (!token) {
    throw new Error('fetchDirections: Mapbox access token is not configured');
  }

  const { avoidMotorway, avoidTolls } = preferences;
  const coords = encodeCoordinates(waypoints);
  // `overview=full` keeps enough vertices to follow the road shape
  // faithfully on display. The corridor filter separately runs
  // `@turf/simplify` (see util/routePlanner/corridor.js) so the filter
  // path doesn't pay for the extra coordinates.
  const params = new URLSearchParams({
    geometries: 'geojson',
    overview: 'full',
    access_token: token,
  });

  const excludes = [];
  if (avoidMotorway) excludes.push('motorway');
  if (avoidTolls) excludes.push('toll');
  if (excludes.length) {
    params.set('exclude', excludes.join(','));
  }
  const url = `${DIRECTIONS_BASE}/${coords}?${params.toString()}`;

  const response = await fetch(url, { signal });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new Error(
      `Mapbox Directions request failed: ${response.status} ${response.statusText} ${body}`,
    );
  }

  const body = await response.json();
  const routes = body?.routes;
  const route = routes?.[0];
  if (!route?.geometry?.coordinates?.length) {
    throw new Error('Mapbox Directions returned no routes');
  }

  return {
    geometry: route.geometry,
    distanceMeters: typeof route.distance === 'number' ? route.distance : 0,
    durationSeconds: typeof route.duration === 'number' ? route.duration : 0,
  };
};
